/**
 * Base Form Class
 * Abstract base class for all form components
 */
export class BaseForm {
  constructor(element, options = {}) {
    this.$form = jQuery(element);
    this.options = {
      validateOnBlur: true,
      validateOnInput: true,
      submitButton: 'button[type="submit"]',
      ajaxUrl: window.hs?.ajaxUrl || '/wp-admin/admin-ajax.php',
      ...options
    };

    this.state = {
      isSubmitting: false,
      errors: new Map(),
      touched: new Set()
    };

    this.$submitButton = this.$form.find(this.options.submitButton);
    this.$inputs = this.$form.find('input, textarea, select');
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupValidation();
  }

  bindEvents() {
    // Prevent default form submission
    this.$form.on('submit', (e) => this.handleSubmit(e));

    // Field validation events
    if (this.options.validateOnBlur) {
      this.$inputs.on('blur', (e) => this.handleFieldBlur(e));
    }

    if (this.options.validateOnInput) {
      this.$inputs.on('input', (e) => this.handleFieldInput(e));
    }
  }

  setupValidation() {
    // Add validation attributes if needed
    this.$inputs.each((_, field) => {
      const $field = jQuery(field);
      const name = $field.attr('name');
      
      if (name) {
        // Track field for validation
        $field.data('field-name', name);
      }
    });
  }

  handleFieldBlur(e) {
    const $field = jQuery(e.target);
    const fieldName = $field.attr('name');
    
    if (fieldName) {
      this.state.touched.add(fieldName);
      this.validateField($field);
    }
  }

  handleFieldInput(e) {
    const $field = jQuery(e.target);
    const fieldName = $field.attr('name');
    
    // Only validate if field has been touched
    if (fieldName && this.state.touched.has(fieldName)) {
      this.validateField($field);
    }
  }

  validateField($field) {
    const value = $field.val()?.trim() || '';
    const fieldName = $field.attr('name');
    const isRequired = $field.prop('required');
    const fieldType = $field.attr('type');
    
    let error = null;

    // Required validation
    if (isRequired && !value) {
      error = window.hs?.strings?.fieldRequired || 'Dit veld is verplicht';
    }
    // Email validation
    else if (fieldType === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        error = window.hs?.strings?.invalidEmail || 'Voer een geldig e-mailadres in';
      }
    }
    // Number validation
    else if (fieldType === 'number' && value) {
      const min = $field.attr('min');
      const max = $field.attr('max');
      const numValue = parseFloat(value);
      
      if (min && numValue < parseFloat(min)) {
        const minError = window.hs?.strings?.minValue || 'Minimale waarde is %s';
        error = minError.replace('%s', min);
      } else if (max && numValue > parseFloat(max)) {
        const maxError = window.hs?.strings?.maxValue || 'Maximale waarde is %s';
        error = maxError.replace('%s', max);
      }
    }

    // Update error state
    if (error) {
      this.setFieldError($field, error);
      this.state.errors.set(fieldName, error);
    } else {
      this.clearFieldError($field);
      this.state.errors.delete(fieldName);
    }

    return !error;
  }

  validateForm() {
    let isValid = true;
    this.state.errors.clear();

    this.$inputs.each((_, field) => {
      const $field = jQuery(field);
      const fieldName = $field.attr('name');
      
      if (fieldName) {
        this.state.touched.add(fieldName);
        if (!this.validateField($field)) {
          isValid = false;
        }
      }
    });

    return isValid;
  }

  setFieldError($field, message) {
    const $wrapper = $field.parent();
    
    // Update field styling
    $field
      .addClass('border-red-500 bg-red-50')
      .removeClass('border-gray-300 bg-gray-200');
    
    // Remove existing error message
    $wrapper.find('.field-error').remove();
    
    // Add new error message
    const $error = jQuery('<div class="field-error text-red-600 text-sm mt-1"></div>')
      .text(message);
    $wrapper.append($error);
  }

  clearFieldError($field) {
    const $wrapper = $field.parent();
    
    // Reset field styling
    $field
      .removeClass('border-red-500 bg-red-50')
      .addClass('border-gray-300 bg-gray-200');
    
    // Remove error message
    $wrapper.find('.field-error').remove();
  }

  showFormError(message) {
    // Remove existing form errors
    this.$form.find('.form-error').remove();
    
    // Add new error
    const $error = jQuery('<div class="form-error bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4"></div>')
      .text(message);
    
    this.$form.prepend($error);
  }

  showFormSuccess(message) {
    // Remove existing messages
    this.$form.find('.form-error, .form-success').remove();
    
    // Add success message
    const $success = jQuery('<div class="form-success bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4"></div>')
      .text(message);
    
    this.$form.prepend($success);
  }

  clearFormMessages() {
    this.$form.find('.form-error, .form-success').remove();
  }

  setSubmitting(isSubmitting) {
    this.state.isSubmitting = isSubmitting;
    this.$submitButton.prop('disabled', isSubmitting);
    
    if (isSubmitting) {
      const loadingText = window.hs?.strings?.submitting || 'Verzenden...';
      this.$submitButton
        .addClass('opacity-75 cursor-not-allowed')
        .html(`<span class="spinner-border spinner-border-sm mr-2"></span> ${loadingText}`);
    } else {
      const originalText = this.$submitButton.data('original-text') || window.hs?.strings?.submit || 'Verstuur';
      this.$submitButton
        .removeClass('opacity-75 cursor-not-allowed')
        .text(originalText);
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    
    if (this.state.isSubmitting) {
      return;
    }

    // Clear previous messages
    this.clearFormMessages();
    
    // Validate all fields
    if (!this.validateForm()) {
      // Focus first error field
      const firstError = this.$form.find('.border-red-500').first();
      if (firstError.length) {
        firstError.focus();
      }
      return;
    }

    // Set submitting state
    this.setSubmitting(true);

    try {
      // Get form data
      const formData = this.getFormData();
      
      // Call child class submit method
      const response = await this.submitForm(formData);
      
      // Handle response
      this.handleResponse(response);
    } catch (error) {
      console.error('Form submission error:', error);
      this.showFormError(
        window.hs?.strings?.generalError || 'Er is een fout opgetreden. Probeer het later opnieuw.'
      );
    } finally {
      this.setSubmitting(false);
    }
  }

  getFormData() {
    const formData = new FormData(this.$form[0]);
    
    // Add any additional data from child classes
    const additionalData = this.getAdditionalData();
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    
    return formData;
  }

  handleResponse(response) {
    if (response.success) {
      this.onSuccess(response);
    } else {
      this.onError(response);
    }
  }

  onSuccess(response) {
    const message = response.data?.message || window.hs?.strings?.formSubmitted || 'Formulier succesvol verzonden';
    this.showFormSuccess(message);
    
    // Reset form if configured
    if (this.options.resetOnSuccess) {
      this.$form[0].reset();
      this.state.touched.clear();
      this.state.errors.clear();
    }
  }

  onError(response) {
    // Handle field-specific errors
    if (response.data?.errors) {
      Object.entries(response.data.errors).forEach(([field, message]) => {
        const $field = this.$form.find(`[name="${field}"]`);
        if ($field.length) {
          this.setFieldError($field, message);
          this.state.errors.set(field, message);
        }
      });
      
      // Focus first error field
      const firstError = this.$form.find('.border-red-500').first();
      if (firstError.length) {
        firstError.focus();
      }
    }
    
    // Show general error message
    const message = response.data?.message || window.hs?.strings?.generalError || 'Er is een fout opgetreden';
    this.showFormError(message);
  }

  // Methods to be implemented by child classes
  getAdditionalData() {
    return {};
  }

  async submitForm(formData) {
    throw new Error('submitForm method must be implemented by child class');
  }

  // Utility methods
  destroy() {
    this.$form.off('submit');
    this.$inputs.off('blur input');
  }
}